/**
 * Pure MapSpec → MapLibre layer/paint builders for the M4 renderer, plus the
 * client-side filter evaluator that powers "showing X of Y". No maplibre-gl
 * import — everything here is plain JSON expressions built on the engine
 * (`@/lib/map`), so the renderer component stays a thin effect layer and these
 * stay unit-testable. Every paint VALUE comes from `getMapTokens()` — no color
 * is ever written here.
 */

import {
  boundsOf,
  categoryColoring,
  colorExpression,
  filterExpression,
  shapeAssignment,
  sizeExpression,
  type Bounds,
  type FilterClause,
  type MapExpr,
  type MapSpec,
  type MapTokens,
  type MarkerShape,
  type MarkerSize,
  type PointSet,
  type ShapeByAssignment,
} from "@/lib/map";
import {
  MARKER_IMAGE_IDS,
  PIN_DOT_IMAGE,
  PIN_GEOMETRY,
  PIN_NATIVE_HEIGHT,
  SHAPE_GEOMETRY,
  SHAPE_NATIVE_SIZE,
  type SdfShape,
} from "./pinIcon";

/** Reserved source/layer ids — one declarative set, toggled by spec. */
export const POINTS_SOURCE = "points";
export const LAYER_POINTS = "points-circle";
/** FAIL-476 teardrop-pin marker layers (SDF symbols): the tinted body + its white head dot. */
export const LAYER_POINTS_PIN = "points-pin";
export const LAYER_POINTS_PIN_DOT = "points-pin-dot";
export const LAYER_CLUSTERS = "points-cluster";
export const LAYER_CLUSTER_COUNT = "points-cluster-count";
export const LAYER_HEAT = "points-heat";
export const LAYER_HIGHLIGHT = "points-highlight";
/** M5 QUERY annotation badges (group-by aggregates / distance-to labels). */
export const ANNOTATIONS_SOURCE = "annotations";
export const LAYER_ANNOTATIONS = "points-annotation";

/** Below this `_confidence` a geocode renders as the hollow low-accuracy variant. */
export const LOW_CONFIDENCE_THRESHOLD = 0.5;

/** Point counts above this get the dense marker size by default. */
const DENSE_POINT_COUNT = 2000;

const lowConfidence: MapExpr = [
  "<",
  ["to-number", ["get", "_confidence"], 1],
  LOW_CONFIDENCE_THRESHOLD,
];

/** The active color-by's values across all points (expression input). */
export function colorValues(pointSet: PointSet, spec: MapSpec): string[] {
  const col = spec.encoding.colorBy?.column;
  if (!col) return [];
  return pointSet.points.map((p) => p.props[col] ?? "");
}

function sizeValues(pointSet: PointSet, spec: MapSpec): string[] {
  const col = spec.encoding.sizeBy?.column;
  if (!col) return [];
  return pointSet.points.map((p) => p.props[col] ?? "");
}

/** Base marker radius (px) — dense datasets drop to the dense size. */
export function baseRadius(pointSet: PointSet, tokens: MapTokens): number {
  const d = pointSet.count > DENSE_POINT_COUNT ? tokens.marker.sizeDense : tokens.marker.size;
  return d / 2;
}

/* ------------------------------------------------------------------ */
/* FAIL-476 marker shape/size resolution                               */
/* ------------------------------------------------------------------ */

/** Circle diameters (px) for the explicit `circle` shape sizes. */
const CIRCLE_DIAMETER: Record<MarkerSize, number> = { s: 9, m: 13, l: 20 };
/** Teardrop-pin heights (CSS px) for the pin shape sizes. */
const PIN_HEIGHT_PX: Record<MarkerSize, number> = { s: 28, m: 36, l: 48 };
/** FAIL-397 center-shape box sizes (CSS px) — the shape fills ~26/36 of the box. */
const SHAPE_BOX_PX: Record<MarkerSize, number> = { s: 24, m: 32, l: 44 };

/** The center-anchored SDF shapes (everything but circle + the bottom-anchored pins). */
const CENTER_SHAPES = new Set<MarkerShape>(["square", "diamond", "triangle", "star", "hexagon"]);

/** Resolved marker shape — absent `marker` renders as the classic circle (grandfathered). */
export function markerShape(spec: MapSpec): MarkerShape {
  return spec.marker?.shape ?? "circle";
}

/** True when the spec draws a teardrop pin (either variant) rather than a circle/shape. */
export function isPinMarker(spec: MapSpec): boolean {
  const s = markerShape(spec);
  return s === "pin" || s === "pin-solid";
}

/** True when the spec draws a center-anchored SDF shape (square/diamond/…/star). */
export function isCenterShapeMarker(spec: MapSpec): boolean {
  return CENTER_SHAPES.has(markerShape(spec));
}

/* ------------------------------------------------------------------ */
/* MAP-24 shape-by — per-value marker shapes                           */
/* ------------------------------------------------------------------ */

/** True when shape-by is active (a column drives per-value marker shapes). */
export function isShapeByActive(spec: MapSpec): boolean {
  return !!spec.encoding.iconBy?.column;
}

/** The active shape-by column's values across all points (assignment + expression input). */
export function shapeByValues(pointSet: PointSet, spec: MapSpec): string[] {
  const col = spec.encoding.iconBy?.column;
  if (!col) return [];
  return pointSet.points.map((p) => p.props[col] ?? "");
}

/** The deterministic value→shape assignment for the active shape-by, or null when off. */
export function shapeByAssignmentFor(pointSet: PointSet, spec: MapSpec): ShapeByAssignment | null {
  if (!isShapeByActive(spec)) return null;
  return shapeAssignment(shapeByValues(pointSet, spec));
}

/** The registered SDF image id for one marker shape (both pins share the teardrop body). */
export function markerImageIdForShape(shape: MarkerShape): string {
  if (shape === "pin" || shape === "pin-solid") return MARKER_IMAGE_IDS.pin;
  return MARKER_IMAGE_IDS[shape as SdfShape | "circle"];
}

/** A pin (either variant) anchors at its tip; the circle + center shapes at their centre. */
function shapeAnchor(shape: MarkerShape): "bottom" | "center" {
  return shape === "pin" || shape === "pin-solid" ? "bottom" : "center";
}

/**
 * A data-driven `match` over the shape-by column: each assigned value maps to
 * `valueFor(itsShape)`, with the Other-bucket shape as the fallback. Collapses to
 * a bare fallback when nothing is assigned (empty column) — a stable, single-
 * value expression either way. Shared by the icon-image, icon-anchor, dot-image,
 * and base-size builders so every per-shape branch is one code path.
 */
function shapeMatch<T>(column: string, a: ShapeByAssignment, valueFor: (shape: MarkerShape) => T): MapExpr {
  const pairs: MapExpr[] = [];
  for (const v of a.order) pairs.push(v, valueFor(a.shapeFor[v]!));
  const fallback = valueFor(a.other);
  return pairs.length > 0 ? ["match", ["get", column], ...pairs, fallback] : fallback;
}

/** True when the marker is an SDF symbol — a non-circle shape, OR any shape-by (mixed shapes). */
export function isSymbolMarker(spec: MapSpec): boolean {
  return isShapeByActive(spec) || markerShape(spec) !== "circle";
}

/** Only the `pin` variant stacks the white head dot; `pin-solid` + shapes omit it. */
export function pinHasDot(spec: MapSpec): boolean {
  return markerShape(spec) === "pin";
}

/**
 * The dot layer must be visible when the global marker is a `pin`, OR whenever
 * shape-by is active (some values may be assigned the dotted pin — the dot's
 * data-driven `icon-image` renders "" for every non-pin value, so no dot shows).
 */
export function dotLayerVisible(spec: MapSpec): boolean {
  return pinHasDot(spec) || isShapeByActive(spec);
}

/** The registered SDF image id for the active symbol marker (pins share one image). */
export function markerImageId(spec: MapSpec): string {
  return markerImageIdForShape(markerShape(spec));
}

/** Pins anchor at the tip (on the point); center shapes anchor at their centre. */
function markerAnchor(spec: MapSpec): "bottom" | "center" {
  return isPinMarker(spec) ? "bottom" : "center";
}

/** Dense datasets shrink SDF markers (parity with the circle's sizeDense drop). */
function markerDenseFactor(pointSet: PointSet): number {
  return pointSet.count > DENSE_POINT_COUNT ? 0.8 : 1;
}

/** Effective pin height (px) the body renders at — chosen size × dense shrink. */
function effectivePinHeightPx(pointSet: PointSet, spec: MapSpec): number {
  const size = spec.marker?.size ?? "m";
  return PIN_HEIGHT_PX[size] * markerDenseFactor(pointSet);
}

/** Effective center-shape box (px) the icon renders at — chosen size × dense shrink. */
function effectiveShapeBoxPx(pointSet: PointSet, spec: MapSpec): number {
  const size = spec.marker?.size ?? "m";
  return SHAPE_BOX_PX[size] * markerDenseFactor(pointSet);
}

/**
 * Base `icon-size` factor for ONE shape at the spec's chosen size (before size-by).
 * Pins scale to their height in the taller pin box; the circle + center shapes to
 * their box — so mixed shape-by shapes read at comparable weights on the map.
 */
function shapeBaseFactor(shape: MarkerShape, pointSet: PointSet, spec: MapSpec): number {
  if (shape === "pin" || shape === "pin-solid") {
    return effectivePinHeightPx(pointSet, spec) / PIN_NATIVE_HEIGHT;
  }
  return effectiveShapeBoxPx(pointSet, spec) / SHAPE_NATIVE_SIZE;
}

/**
 * Explicit `circle`-marker radius (px), or null when the spec doesn't pin a
 * circle size (absent marker, or a pin spec) — in which case the circle layer
 * keeps the historical token radius so grandfathered maps are unchanged.
 */
function circleRadiusPx(spec: MapSpec): number | null {
  if (spec.marker?.shape === "circle") return CIRCLE_DIAMETER[spec.marker.size] / 2;
  return null;
}

/**
 * The full data-driven paint block for the unclustered points layer. The white
 * ring is structural (§11) — the low-confidence variant INVERTS it (paper fill,
 * colored stroke) rather than removing it, so geocode accuracy stays visible
 * without a second layer.
 */
export function pointsPaint(pointSet: PointSet, spec: MapSpec, tokens: MapTokens): Record<string, MapExpr> {
  const color = colorExpression(
    spec.encoding.colorBy,
    colorValues(pointSet, spec),
    tokens.palette,
    tokens.paletteOther,
    tokens.sequential,
    tokens.marker.fill,
  );
  // An explicit circle size scales the size-by range around the chosen radius;
  // otherwise (absent marker) keep the exact historical range so old maps render
  // byte-identical.
  const chosen = circleRadiusPx(spec);
  const radius = sizeExpression(
    spec.encoding.sizeBy,
    sizeValues(pointSet, spec),
    chosen !== null ? [chosen * 0.8, chosen * 2.6] : [tokens.marker.sizeDense / 2, tokens.marker.sizeEmphasized],
    chosen ?? baseRadius(pointSet, tokens),
  );
  return {
    "circle-color": ["case", lowConfidence, tokens.marker.lowConfidenceFill, color],
    "circle-radius": radius,
    "circle-stroke-width": [
      "case",
      lowConfidence,
      tokens.marker.lowConfidenceStrokeWidth,
      tokens.marker.ringWidth,
    ],
    "circle-stroke-color": ["case", lowConfidence, color, tokens.marker.ring],
  };
}

/** Layer filter for unclustered points: never a cluster, plus the user filters. */
export function pointsFilter(spec: MapSpec): MapExpr {
  const notCluster: MapExpr = ["!", ["has", "point_count"]];
  const user = filterExpression(spec.filters);
  return user ? ["all", notCluster, user] : notCluster;
}

/* ------------------------------------------------------------------ */
/* SDF symbol marker layers — teardrop pin (FAIL-476) + shapes (FAIL-397) */
/* ------------------------------------------------------------------ */

/** `icon-size` factor (+ size-by scaling) for a single-shape symbol marker at its chosen size. */
function markerIconSize(pointSet: PointSet, spec: MapSpec): MapExpr {
  const base = shapeBaseFactor(markerShape(spec), pointSet, spec);
  // Size-by scales the icon multiplicatively around its base, mirroring circles.
  return sizeExpression(spec.encoding.sizeBy, sizeValues(pointSet, spec), [base * 0.75, base * 1.7], base);
}

/**
 * The size-by MULTIPLIER (around 1.0), independent of any base. `sizeExpression`
 * with a base of 1 yields exactly this: `1` when there is no size-by, else an
 * interpolation over [0.75, 1.7]. Shape-by multiplies each shape's base factor by
 * this, so per-value bases and the size-by scaling compose (parity with circles).
 */
function sizeByMultiplier(pointSet: PointSet, spec: MapSpec): MapExpr {
  return sizeExpression(spec.encoding.sizeBy, sizeValues(pointSet, spec), [0.75, 1.7], 1);
}

/** Per-value `icon-size` for shape-by: each value's shape base × the size-by multiplier. */
function shapeByIconSize(pointSet: PointSet, spec: MapSpec, a: ShapeByAssignment): MapExpr {
  const column = spec.encoding.iconBy!.column;
  const baseMatch = shapeMatch(column, a, (shape) => shapeBaseFactor(shape, pointSet, spec));
  const mult = sizeByMultiplier(pointSet, spec);
  return mult === 1 ? baseMatch : ["*", baseMatch, mult];
}

/** Assemble the five stable symbol-layout keys from a resolved image/anchor/size. */
function symbolLayout(image: MapExpr, anchor: MapExpr, size: MapExpr): Record<string, MapExpr> {
  return {
    "icon-image": image,
    "icon-size": size,
    "icon-anchor": anchor,
    "icon-allow-overlap": true,
    "icon-ignore-placement": true,
  };
}

/**
 * Symbol layout for the marker BODY. Single-shape: one image, one anchor, one
 * size (byte-identical to the pre-MAP-24 output). Shape-by: data-driven
 * icon-image (value → its shape's SDF), icon-anchor (pin values bottom, else
 * centre), and icon-size (per-shape base × size-by). Every branch emits the same
 * five keys, so a restyle diff always overwrites — no stale GL state on switch.
 */
export function markerBodyLayout(pointSet: PointSet, spec: MapSpec): Record<string, MapExpr> {
  const a = shapeByAssignmentFor(pointSet, spec);
  if (a) {
    const column = spec.encoding.iconBy!.column;
    return symbolLayout(
      shapeMatch(column, a, markerImageIdForShape),
      shapeMatch(column, a, shapeAnchor),
      shapeByIconSize(pointSet, spec, a),
    );
  }
  return symbolLayout(markerImageId(spec), markerAnchor(spec), markerIconSize(pointSet, spec));
}

/**
 * Symbol layout for the pin's white head-DOT overlay. Single-shape: the dot image
 * at the body's anchor/size (drawn only when the layer is visible, i.e. a `pin`).
 * Shape-by: the dot renders only for values assigned the dotted `pin` (every
 * other value maps to "" → no icon, MapLibre treats empty as no image), anchored
 * at the tip and sized to match the pin body. Same five keys as the body layout.
 */
export function markerDotLayout(pointSet: PointSet, spec: MapSpec): Record<string, MapExpr> {
  const a = shapeByAssignmentFor(pointSet, spec);
  if (a) {
    const column = spec.encoding.iconBy!.column;
    return symbolLayout(
      shapeMatch(column, a, (shape) => (shape === "pin" ? PIN_DOT_IMAGE : "")),
      "bottom",
      shapeByIconSize(pointSet, spec, a),
    );
  }
  return symbolLayout(PIN_DOT_IMAGE, markerAnchor(spec), markerIconSize(pointSet, spec));
}

/**
 * Symbol body paint: `icon-color` tinted by color-by (same expression as the
 * circle fill), wrapped in a white halo — the §11 structural ring. Low
 * confidence INVERTS it (paper body, colored halo), matching the hollow circle.
 * Identical for the pin and every FAIL-397 shape.
 */
export function markerSymbolPaint(pointSet: PointSet, spec: MapSpec, tokens: MapTokens): Record<string, MapExpr> {
  const color = colorExpression(
    spec.encoding.colorBy,
    colorValues(pointSet, spec),
    tokens.palette,
    tokens.paletteOther,
    tokens.sequential,
    tokens.marker.pin.fill,
  );
  return {
    "icon-color": ["case", lowConfidence, tokens.marker.lowConfidenceFill, color],
    "icon-halo-color": ["case", lowConfidence, color, tokens.marker.pin.dot],
    "icon-halo-width": tokens.marker.ringWidth,
    "icon-halo-blur": 0,
  };
}

/** Head-dot paint (pin only): white on a normal pin; the category color on a paper (low-confidence) pin. */
export function pinDotPaint(pointSet: PointSet, spec: MapSpec, tokens: MapTokens): Record<string, MapExpr> {
  const color = colorExpression(
    spec.encoding.colorBy,
    colorValues(pointSet, spec),
    tokens.palette,
    tokens.paletteOther,
    tokens.sequential,
    tokens.marker.pin.fill,
  );
  return {
    "icon-color": ["case", lowConfidence, color, tokens.marker.pin.dot],
  };
}

/**
 * Cluster aggregation properties tracking per-category member counts for the
 * top palette-slotted categories, so the cluster bubble can wear the dominant
 * category's color. Keyed `_cat_<i>` in assignment order.
 */
export function clusterCategoryProperties(column: string, categories: string[]): Record<string, MapExpr> {
  const props: Record<string, MapExpr> = {};
  categories.forEach((value, i) => {
    props[`_cat_${i}`] = ["+", ["case", ["==", ["get", column], value], 1, 0]];
  });
  return props;
}

/**
 * Dominant-category cluster color: the palette color of the category with the
 * highest member count (ties resolve toward the globally more frequent
 * category). Clusters made entirely of "Other" values keep the ink fill.
 */
export function clusterColorExpression(categories: string[], colorFor: Record<string, string>, inkFill: string): MapExpr {
  if (categories.length === 0) return inkFill;
  const count = (i: number): MapExpr => ["to-number", ["get", `_cat_${i}`], 0];
  const branches: MapExpr[] = [];
  categories.forEach((value, i) => {
    const others = categories
      .map((_, j) => j)
      .filter((j) => j !== i)
      .map((j): MapExpr => [">=", count(i), count(j)]);
    branches.push(others.length > 0 ? ["all", ...others] : true, colorFor[value] ?? inkFill);
  });
  const total: MapExpr =
    categories.length === 1 ? count(0) : ["+", ...categories.map((_, i) => count(i))];
  return ["case", ["==", total, 0], inkFill, ...branches, inkFill];
}

/**
 * The categorical coloring driving cluster tinting (same deterministic
 * assignment as the point colors). Null when color-by isn't categorical.
 */
export function clusterCategorySignature(
  pointSet: PointSet,
  spec: MapSpec,
  tokens: MapTokens,
): { column: string; categories: string[]; colorFor: Record<string, string> } | null {
  const colorBy = spec.encoding.colorBy;
  if (!colorBy || colorBy.mode !== "categorical") return null;
  const coloring = categoryColoring(
    colorValues(pointSet, spec),
    tokens.palette,
    tokens.paletteOther,
    colorBy.overrides,
  );
  return { column: colorBy.column, categories: coloring.order, colorFor: coloring.colorFor };
}

/** Ink-stamp cluster bubble paint (§11) — dominant-category tint when categorical. */
export function clusterPaint(pointSet: PointSet, spec: MapSpec, tokens: MapTokens): Record<string, MapExpr> {
  const sig = clusterCategorySignature(pointSet, spec, tokens);
  const [s1, s2, s3, s4] = tokens.cluster.sizes;
  return {
    "circle-color": sig
      ? clusterColorExpression(sig.categories, sig.colorFor, tokens.cluster.fill)
      : tokens.cluster.fill,
    "circle-radius": ["step", ["get", "point_count"], s1! / 2, 25, s2! / 2, 100, s3! / 2, 500, s4! / 2],
    "circle-stroke-color": tokens.cluster.ring,
    "circle-stroke-width": tokens.cluster.ringWidth,
  };
}

/** Heat ramp: density 0 → transparent, then the plum ramp (tokens.heat). */
export function heatPaint(spec: MapSpec, tokens: MapTokens): Record<string, MapExpr> {
  const ramp = tokens.heat;
  const stops: MapExpr[] = [];
  ramp.forEach((color, i) => {
    stops.push(i / (ramp.length - 1), color);
  });
  const weightColumn = spec.heat.weightColumn;
  return {
    "heatmap-color": ["interpolate", ["linear"], ["heatmap-density"], ...stops],
    "heatmap-weight": weightColumn ? ["max", ["to-number", ["get", weightColumn], 0], 0] : 1,
    "heatmap-intensity": ["interpolate", ["linear"], ["zoom"], 0, 0.6, 9, 1.4],
    "heatmap-radius": ["interpolate", ["linear"], ["zoom"], 0, 4, 9, 24, 14, 40],
    "heatmap-opacity": 0.85,
  };
}

/**
 * Highlight overlay (M5 `highlight()`): halo + ink emphasized ring. For pins the
 * halo rides up to the HEAD (a viewport-space translate above the anchored tip)
 * and matches the head's radius, so selection reads on the pin — not the ground.
 */
export function highlightPaint(pointSet: PointSet, spec: MapSpec, tokens: MapTokens): Record<string, MapExpr> {
  const haloPad = tokens.marker.selectedHaloSize / 2;
  // EVERY branch returns circle-translate + its anchor so the restyle diff (which
  // only overwrites keys present in the new paint) always RESETS a prior offset —
  // otherwise a pin→circle switch leaves the pin's upward translate on the halo.
  const common = {
    "circle-color": tokens.marker.selectedHalo,
    "circle-opacity": 0.6,
    "circle-stroke-color": tokens.marker.selectedRing,
    "circle-stroke-width": tokens.marker.ringWidth,
    "circle-translate": [0, 0],
    "circle-translate-anchor": "map",
  };
  // Shape-by (MAP-24) mixes shapes per value — heights vary and most are
  // center-anchored, so ring the point (no fixed head lift), sized to the base
  // shape box so the halo clears the largest center shape. Same reset keys as the
  // other branches, so a switch onto/off shape-by never strands a stale offset.
  if (isShapeByActive(spec)) {
    const scale = effectiveShapeBoxPx(pointSet, spec) / SHAPE_GEOMETRY.nativeSize;
    return { ...common, "circle-radius": SHAPE_GEOMETRY.boundingRadius * scale + haloPad };
  }
  if (isPinMarker(spec)) {
    const scale = effectivePinHeightPx(pointSet, spec) / PIN_GEOMETRY.nativeHeight;
    const radius = PIN_GEOMETRY.headRadius * scale + haloPad;
    // circle-translate can't be data-driven, so only lift the halo to the head
    // when every pin is the same height. With size-by, pin heights vary per
    // feature — ring the anchored tip (the true point) instead of mis-placing a
    // single fixed head offset.
    if (!spec.encoding.sizeBy) {
      return {
        ...common,
        "circle-radius": radius,
        "circle-translate": [0, -PIN_GEOMETRY.headCenterFromTip * scale],
        "circle-translate-anchor": "viewport",
      };
    }
    return { ...common, "circle-radius": radius };
  }
  if (isCenterShapeMarker(spec)) {
    // Center-anchored shapes sit ON the point — ring them there (no translate),
    // sized to the shape's bounding radius.
    const scale = effectiveShapeBoxPx(pointSet, spec) / SHAPE_GEOMETRY.nativeSize;
    return { ...common, "circle-radius": SHAPE_GEOMETRY.boundingRadius * scale + haloPad };
  }
  const r = circleRadiusPx(spec) ?? baseRadius(pointSet, tokens);
  return { ...common, "circle-radius": r + haloPad };
}

/** Filter for the highlight overlay — matches nothing when no ids are set. */
export function highlightFilter(spec: MapSpec, highlightIds: string[] | undefined): MapExpr {
  const notCluster: MapExpr = ["!", ["has", "point_count"]];
  if (!highlightIds || highlightIds.length === 0) return ["all", notCluster, false];
  const inIds: MapExpr = ["in", ["get", "_id"], ["literal", highlightIds]];
  const user = filterExpression(spec.filters);
  return user ? ["all", notCluster, inIds, user] : ["all", notCluster, inIds];
}

/* ------------------------------------------------------------------ */
/* M5 QUERY map effects: annotation badges + fit-to-matches            */
/* ------------------------------------------------------------------ */

/** A GeoJSON point feature carrying a QUERY annotation badge. */
export interface AnnotationFeatureCollection {
  type: "FeatureCollection";
  features: Array<{
    type: "Feature";
    geometry: { type: "Point"; coordinates: [number, number] };
    properties: { _id: string; text: string };
  }>;
}

/**
 * Build the annotation badge source: one label feature per `{id,text}` whose id
 * resolves to a loaded point (group-by aggregates, distance-to labels). Pure —
 * no maplibre import, so the renderer stays a thin effect layer.
 */
export function annotationFeatures(
  pointSet: PointSet,
  annotations: Array<{ id: string; text: string }>,
): AnnotationFeatureCollection {
  const byId = new Map(pointSet.points.map((p) => [p.id, p]));
  const features: AnnotationFeatureCollection["features"] = [];
  for (const a of annotations) {
    const p = byId.get(a.id);
    if (!p) continue;
    features.push({
      type: "Feature",
      geometry: { type: "Point", coordinates: [p.lng, p.lat] },
      properties: { _id: p.id, text: a.text },
    });
  }
  return { type: "FeatureCollection", features };
}

/** Annotation badge text paint — ink label with a paper halo (tokens only). */
export function annotationPaint(tokens: MapTokens): Record<string, MapExpr> {
  return {
    "text-color": tokens.label.ink,
    "text-halo-color": tokens.label.halo,
    "text-halo-width": 1.5,
    "text-halo-blur": 0.5,
  };
}

/**
 * Bounds enclosing exactly the given ids — the target for the `fit` QUERY
 * effect (nearest-N, etc.). Null when nothing matches (caller leaves the view).
 */
export function boundsForIds(pointSet: PointSet, ids: string[]): Bounds | null {
  if (ids.length === 0) return null;
  const wanted = new Set(ids);
  return boundsOf(pointSet.points.filter((p) => wanted.has(p.id)));
}

/* ------------------------------------------------------------------ */
/* Client-side filter evaluation (mirrors clauseExpression semantics)  */
/* ------------------------------------------------------------------ */

function toNum(v: string | undefined): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** One clause against a point's props — same semantics as the layer filter. */
export function matchesClause(props: Record<string, string>, clause: FilterClause): boolean {
  const raw = props[clause.column];
  const { op, value } = clause;
  switch (op) {
    case "is":
      return raw === String(value ?? "");
    case "is_not":
      return raw !== String(value ?? "");
    case "in":
      return Array.isArray(value) ? (value as string[]).includes(raw ?? "") : false;
    case "gte":
      return toNum(raw) >= Number(value ?? 0);
    case "lte":
      return toNum(raw) <= Number(value ?? 0);
    case "between": {
      const [a, b] = Array.isArray(value) ? value : [0, 0];
      const n = toNum(raw);
      return n >= Number(a) && n <= Number(b);
    }
    case "contains":
      return String(raw ?? "").includes(String(value ?? ""));
    case "empty":
      return (raw ?? "") === "";
    case "not_empty":
      return (raw ?? "") !== "";
    default:
      return true;
  }
}

export function matchesFilters(props: Record<string, string>, filters: FilterClause[]): boolean {
  return filters.every((clause) => matchesClause(props, clause));
}

/** How many of the set's points pass the spec's filters (drives "X of Y"). */
export function filteredCount(pointSet: PointSet, filters: FilterClause[]): number {
  if (filters.length === 0) return pointSet.count;
  let n = 0;
  for (const p of pointSet.points) if (matchesFilters(p.props, filters)) n += 1;
  return n;
}

/* ------------------------------------------------------------------ */
/* Value formatting (popups, tooltips, legend breaks)                  */
/* ------------------------------------------------------------------ */

const CURRENCY_RE = /^\s*[$€£]\s?-?[\d,]+(\.\d+)?\s*$/;

/** Popup/tooltip display formatting: thousands separators, money passthrough. */
export function formatCellValue(raw: string, type?: string): string {
  const trimmed = raw.trim();
  if (trimmed === "") return "—";
  if (CURRENCY_RE.test(trimmed)) {
    const symbol = trimmed[0]!;
    const n = Number(trimmed.slice(1).replace(/,/g, "").trim());
    if (Number.isFinite(n)) {
      return `${symbol}${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
    }
  }
  if (type === "number") {
    const n = Number(trimmed.replace(/,/g, ""));
    if (Number.isFinite(n)) return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return raw;
}

/** Compact numeric-legend break label. */
export function formatBreak(n: number): string {
  if (!Number.isFinite(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 10000) return n.toLocaleString(undefined, { notation: "compact", maximumFractionDigits: 1 });
  if (abs >= 100 || Number.isInteger(n)) return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}
